import { appendIf, isEmailValid } from '@btime/helpers'
import { assoc, compose, map, pick } from 'ramda'
import { gql, useMutation } from '@apollo/client'
import { useSessionPermission } from '@btime/react-session'
import { useTranslation } from 'react-i18next'

import { accountsClient } from '../../../../utils/apollo/api'
import { cleanFieldValuesRequest, validateFieldValues } from '../../../../utils/helpers/fields'
import { useCloseModal } from '../../../../components/modals/ModalsContext'
import * as tracker from '@btime/tracker'

const MUTATION = gql`
  mutation UpsertUsers($inputs: [UserInput]) {
    upsertUsers(inputs: $inputs) {
      id
    }
  }
`

const cleanWorkShifts = workShifts => {
  return workShifts.map(workShift => {
    return workShift.map(workShift => {
      return {
        start: workShift.start,
        end: workShift.end
      }
    })
  })
}

const cleanContract = compose(
  pick(['groupId', 'contractGroupIds', 'profileId', 'fieldValues', 'workShifts']),
  item => assoc('groupId', (!Array.isArray(item.group) && item.group?.id) || null, item),
  item =>
    assoc(
      'contractGroupIds',
      Array.isArray(item.groups) && item.groups?.length ? item?.groups?.map(group => group.id) : null,
      item
    ),
  item => assoc('profileId', item.profile?.id, item),
  item => assoc('fieldValues', cleanFieldValuesRequest(item.fieldValues || []), item),
  item => assoc('workShifts', JSON.stringify(cleanWorkShifts(item.workShifts || [])), item)
)

const cleanRequest = map(
  compose(
    pick(['id', 'name', 'email', 'contract', 'fieldValues']), //
    item => assoc('contract', cleanContract(item.contract || {}), item)
  )
)

const validate = (form, requireUserGroup, setErrors, t) => {
  const errors = form.reduce(
    (errors, item, index) => [
      ...errors,
      ...appendIf(!item.name, { path: [index, 'name'], message: t('validations:required') }),
      ...appendIf(!isEmailValid(item.email), {
        path: [index, 'email'],
        message: t('validations:invalidEmail')
      }),
      ...appendIf(!item.email, { path: [index, 'email'], message: t('validations:required') }),

      ...appendIf(!item.contract?.profile?.id, {
        path: [index, 'contract', 'profile'],
        message: t('validations:required')
      }),

      ...appendIf(
        !Array.isArray(item.contract?.group) &&
          !item.contract?.group?.id &&
          requireUserGroup &&
          !Array.isArray(item.contract?.groups) &&
          !item.contract?.groups?.length,
        {
          path: ['contract', 'group'],
          message: t('validations:required')
        }
      ),
      ...appendIf(Array.isArray(item.contract?.groups) && !item.contract?.groups?.length && requireUserGroup, {
        path: ['contract', 'groups'],
        message: t('validations:required')
      }),

      ...validateFieldValues(form, [index, 'contract', 'fieldValues'])
    ],
    []
  )

  setErrors(errors)
  return errors.length === 0
}

export const useSaveUsers = () => {
  const { t } = useTranslation()
  const requireUserGroup = useSessionPermission('requireUserGroup')
  const closeModal = useCloseModal()
  const [request, state] = useMutation(MUTATION, { refetchQueries: ['Users'], client: accountsClient })

  const handleRequest = (form, actions) => {
    const inputs = cleanRequest(form)
    const { setErrors } = actions
    const tCombined = key => {
      if (key.startsWith('validations:')) {
        return t(`validations:${key.replace('validations:', '')}`)
      }
      return t(key)
    }
    const isValid = validate(form, requireUserGroup, setErrors, tCombined)

    if (isValid) {
      return request({ variables: { inputs } }).then(resp => {
        if (!resp.errors) {
          closeModal()
          tracker.signUp(inputs)
        }
      })
    }
  }

  return [handleRequest, state]
}
